import { action, extendObservable } from 'mobx';
import PropTypes from 'prop-types';
import { apiCall } from '../API';
import { emailDomainValidator } from '../email';
import AsyncActionStore from './AsyncActionStore';
import FlagStore from './FlagStore';
import RadioButtonStore from './RadioButtonStore';
import UrlBulkEditorStore from './UrlBulkEditorStore';
import BulkEditorStore from './BulkEditorStore';

export default class EmailRegStore {
    static OPTION_SAML_SSO = 'OPTION_SAML_SSO';
    static OPTION_CODES = 'OPTION_CODES';
    static OPTION_LINKS = 'OPTION_LINKS';
    static INVALID_DOMAIN_VALIDATOR_INDEX = 0;
    static DUPLICATE_DOMAIN_VALIDATOR_INDEX = 1;

    static PropType = PropTypes.shape({
        saveStatus: AsyncActionStore.PropType.isRequired,
        verifyStatus: AsyncActionStore.PropType.isRequired,
        modal: FlagStore.PropType.isRequired,
        domainsModal: FlagStore.PropType.isRequired,
        emailDisabled: PropTypes.bool.isRequired,
        registrationType: PropTypes.oneOf([
            EmailRegStore.OPTION_SAML_SSO,
            EmailRegStore.OPTION_CODES,
            EmailRegStore.OPTION_LINKS,
        ]),
        primaryDomain: PropTypes.string.isRequired,
        browserRegistrationEnabled: FlagStore.PropType.isRequired,
        registrationTypeStore: RadioButtonStore.PropType.isRequired,
        domainsBulkEditor: UrlBulkEditorStore.PropType.isRequired,
        exitConfirmationModal: FlagStore.PropType.isRequired,

        hasChanged: PropTypes.bool.isRequired,
        samlSSOEnabled: FlagStore.PropType.isRequired,
        configStatus: AsyncActionStore.PropType.isRequired,

        close: PropTypes.func.isRequired,
        setFromObj: PropTypes.func.isRequired,
        save: PropTypes.func.isRequired,
        verifyDomains: PropTypes.func.isRequired,
        loadConfigurations: PropTypes.func.isRequired,
    });


    constructor(data = {}) {
        extendObservable(this, /** @class EmailRegStore */ {
            apiSave: apiCall('/console/registration_options/set_email_reg', { isUserAction: true }),
            saveStatus: new AsyncActionStore(),
            apiVerifyDomains: apiCall('/console/registration_options/verify_email_domains', { isUserAction: true }),
            apiLoadMetaConfig: apiCall('/console/meta/config'),
            verifyStatus: new AsyncActionStore(),
            modal: new FlagStore(false),
            domainsModal: new FlagStore(false),
            browserRegistrationEnabled: new FlagStore(false),
            emailDisabled: true,
            registrationType: null,
            primaryDomain: '',
            registrationTypeStore: new RadioButtonStore(),
            alreadyUsedDomains: [],
            invalidEmailDomains: [],
            domainsBulkEditor: new UrlBulkEditorStore({
                margin: 0,
                bulkEditorStore: new BulkEditorStore({
                    validators: [
                        (value) => emailDomainValidator(value) && !this.invalidEmailDomains.includes(value),
                        (value) => !this.alreadyUsedDomains.includes(value),
                    ],
                }),
            }),
            exitConfirmationModal: new FlagStore(false),
            samlSSOEnabled: new FlagStore(false),
            configStatus: new AsyncActionStore(),

            get hasChanged() {
                return this.emailDisabled === this.browserRegistrationEnabled.value ||
                    (!this.emailDisabled && this.registrationType !== this.registrationTypeStore.value) ||
                    this.domainsBulkEditor.bulkEditorStore.hasChanged();
            },
        }, data);
    }

    close = action(() => {
        if (this.hasChanged) {
            this.exitConfirmationModal.open();
        } else {
            this.modal.close();
            this.domainsModal.close();
        }
    });

    setFromObj = action(({ useAccessCodes, useSsoLogin, emailDisabled, emailDomains, primaryDomain }) => {
        let newValue = EmailRegStore.OPTION_LINKS;
        if (useSsoLogin) {
            newValue = EmailRegStore.OPTION_SAML_SSO;
        } else if (useAccessCodes) {
            newValue = EmailRegStore.OPTION_CODES;
        }
        this.registrationType = newValue;
        this.registrationTypeStore.setValue(newValue);
        this.emailDisabled = emailDisabled;
        this.browserRegistrationEnabled.setValue(!emailDisabled);
        this.domainsBulkEditor.bulkEditorStore
            .setUrlPatterns(emailDomains.filter((domain) => domain !== primaryDomain));
        this.primaryDomain = primaryDomain;
    });

    toObj() {
        const domains = this.domainsBulkEditor.bulkEditorStore.urlPatterns.map((domain) => domain.trim());
        const primaryDomain = this.primaryDomain ? this.primaryDomain.trim() : '';
        const data = {
            emailDisabled: !this.browserRegistrationEnabled.value,
            emailDomains: [
                ...domains.filter((domain, index) => domain && domain !== primaryDomain &&
                    !domains.some((otherDomain, otherIndex) => otherIndex < index && otherDomain === domain)),
            ],
            primaryDomain,
        };
        if (primaryDomain) {
            data.emailDomains.push(primaryDomain);
        }
        if (this.browserRegistrationEnabled.value) {
            if (this.registrationTypeStore.value === EmailRegStore.OPTION_SAML_SSO) {
                data.useSsoLogin = true;
            } else if (this.registrationTypeStore.value === EmailRegStore.OPTION_CODES) {
                data.useAccessCodes = true;
            } else {
                data.useSsoLogin = false;
                data.useAccessCodes = false;
            }
        }
        return data;
    }

    save = action(() => {
        if (this.saveStatus.isLoading) {
            return;
        }
        this.saveStatus.setLoading();
        const data = this.toObj();
        return this.apiSave(data)
            .then(action(() => {
                this.setFromObj(data);
                this.saveStatus.onSuccess();
                this.modal.close();
                this.domainsModal.close();
                return data;
            }))
            .catch((e) => {
                this.saveStatus.setFailed(e);
                throw e;
            });
    });

    verifyDomains = action(() => {
        if (this.verifyStatus.isLoading) {
            return;
        }
        this.verifyStatus.setBase();
        if (!this.domainsBulkEditor.bulkEditorStore.isValid()) {
            return;
        }
        this.verifyStatus.setLoading();
        return this.apiVerifyDomains(this.toObj())
            .then(action((data) => {
                this.verifyStatus.setBase();
                this.alreadyUsedDomains = data.alreadyUsedDomains || [];
                this.invalidEmailDomains = data.invalidEmailDomains || [];
                this.domainsBulkEditor.bulkEditorStore.updateUrlBulkInfo();
                return this.alreadyUsedDomains.length === 0 && this.invalidEmailDomains.length === 0;
            }))
            .catch((e) => {
                this.verifyStatus.setFailed(e);
                throw e;
            });
    });

    loadConfigurations = action(() => {
        if (!this.configStatus.isLoading) {
            this.configStatus.setLoading(this.apiLoadMetaConfig()
                .then((config) => {
                    this.samlSSOEnabled.setValue(config.metaEnabled);
                }));
        }
        return this.configStatus.promise;
    });
}
